import {
  ICompany,
  IAttendanceEmployee,
  IAttendanceEmployeeAttendance,
  AttendanceEmployeeStatusEnum,
  IHttpError,
  useAttendanceDeleteEmployeeMutation
} from '@/@data'
import { useRouteQuery } from '@/hooks/useRouteQuery'
import { useTranslator } from '@/hooks/useTranslator'
import { Box, IconButton, Tooltip, Zoom } from '@mui/material'
import { TablePagination, TablePaginationProps } from '@/@core/components/mui/TablePagination'
import { UserAvatar } from '@/components/common/UserAvatar'
import { useNotification } from '@/hooks'
import dayjs from 'dayjs'
import { useParams } from 'react-router-dom'

interface DetailAttendanceCompanyTableProps {
  data: IAttendanceEmployee[]
  meta: unknown
  month?: string
  onRowClick?: (employer: ICompany) => void
  onEdit?: (employer: ICompany) => void
  refetch: () => void
  dataNormal: any
}

// const getAttendanceStatus = (attendance: IAttendanceEmployeeAttendance[], date: number) => {
//   const dayAttendance = attendance.find(a => a.date === date)

//   return dayAttendance?.status || AttendanceEmployeeStatusEnum.NonWorkingDay
// }

const getAttendanceDotColor = (status: string) => {
  switch (status) {
    case AttendanceEmployeeStatusEnum.WorkingDay:
      return '#577dfd'
    case AttendanceEmployeeStatusEnum.Absent:
      return '#ff7074'
    case AttendanceEmployeeStatusEnum.NonWorkingDay:
      return '#c0bec5'
    default:
      return ''
  }
}

// const getNextStatus = (currentStatus: string) => {
//   switch (currentStatus) {
//     case AttendanceEmployeeStatusEnum.WorkingDay:
//       return AttendanceEmployeeStatusEnum.Absent
//     case AttendanceEmployeeStatusEnum.Absent:
//       return AttendanceEmployeeStatusEnum.NonWorkingDay
//     case AttendanceEmployeeStatusEnum.NonWorkingDay:
//       return AttendanceEmployeeStatusEnum.WorkingDay
//     default:
//       return AttendanceEmployeeStatusEnum.WorkingDay
//   }
// }

const countWorkingDays = (attendance: IAttendanceEmployeeAttendance[]) => {
  return attendance.filter(a => a.status === AttendanceEmployeeStatusEnum.WorkingDay).length
}

export const DetailAttendanceCompanyTable = ({
  data,
  meta,
  refetch,
  dataNormal
}: DetailAttendanceCompanyTableProps) => {
  const { searchQuery } = useRouteQuery()
  const { __ } = useTranslator()
  const { id } = useParams<{ id: string }>()
  const n = useNotification()
  const { ConfirmDialog } = n
  const dataWithId = data?.map(item => ({ ...item, id: item.id || item.user_id })) || []

  // Get current day and check if viewing current month
  const monthParam = searchQuery.get('month') || dayjs().format('YYYY-MM')
  const selectedMonth = dayjs(monthParam)
  const currentMonth = dayjs()
  const isCurrentMonth = selectedMonth.isSame(currentMonth, 'month')
  const currentDay = isCurrentMonth ? currentMonth.date() : null

  // Build header days from normalized data if available, otherwise fallback to calendar month
  const headerWorkingListRaw: Array<{ date: number; day_of_month?: string }> =
    (dataNormal?.attendance?.workingList as Array<{ date: number; day_of_month?: string }> | undefined) ||
    Array.from({ length: selectedMonth.daysInMonth() }, (_, i) => ({ date: i + 1 }))

  // Move dates that no employee has to the end so blanks appear at the end
  const availableDates = new Set<number>()

  for (const emp of dataWithId) {
    for (const a of emp.attendance || []) {
      if (typeof a?.date === 'number') availableDates.add(a.date)
    }
  }

  const headerWorkingList = [
    ...headerWorkingListRaw.filter(d => availableDates.has(d.date)),
    ...headerWorkingListRaw.filter(d => !availableDates.has(d.date))
  ]

  // const { mutate: attendanceStatusUpdateEmployeeMutation } = useAttendanceStatusUpdateEmployeeMutation()

  // const handleUpdateAttendanceStatus = (userId: number, currentAttendance: IAttendanceEmployeeAttendance[], day: number) => {
  //   const currentStatus = getAttendanceStatus(currentAttendance, day)
  //   const nextStatus = getNextStatus(currentStatus)

  //   const updatedAttendance = currentAttendance.slice()
  //   const existingIndex = updatedAttendance.findIndex(a => a.date === day)

  //   if (existingIndex !== -1) {
  //     updatedAttendance[existingIndex] = { ...updatedAttendance[existingIndex], status: nextStatus }
  //   } else {
  //     updatedAttendance.push({ date: day, status: nextStatus })
  //   }

  //   const month = searchQuery.get('month') || new Date().toISOString().substring(0, 7)

  //   attendanceStatusUpdateEmployeeMutation({
  //     companyId: Number(searchQuery.get('companyId')) || Number(id),
  //     userId,
  //     data: {
  //       month: month,
  //       attendance: updatedAttendance
  //     }
  //   }, {
  //     onSuccess: () => {
  //       n.success(__('Attendance status updated successfully'))
  //       refetch()
  //     },
  //     onError: (error: IHttpError) => {
  //       n.error(error.message)
  //     }
  //   })
  // }

  const enum RowBackgroundForStatusEnum {
    MissingSystem = 'missing_system',
    MissingFile = 'missing_file',
    Unreconciled = 'unreconciled',
    Reconciled = 'reconciled'
  }

  const RowBackgroundForStatus = {
    [RowBackgroundForStatusEnum.MissingSystem]: '#ff4c5114',
    [RowBackgroundForStatusEnum.MissingFile]: '#ff9f4314',
    [RowBackgroundForStatusEnum.Unreconciled]: '#fff',
    [RowBackgroundForStatusEnum.Reconciled]: '#fff'
  }

  const { mutate: attendanceDeleteEmployeeMutation } = useAttendanceDeleteEmployeeMutation()

  const handleDeleteEmployee = (id: string) => {
    n.confirm(__('Are you sure you want to delete this employee?'), {
      onConfirm: () => {
        attendanceDeleteEmployeeMutation(
          { id: Number(id) },
          {
            onSuccess: () => {
              n.success(__('Employee deleted successfully'))
              refetch()
            },
            onError: (error: IHttpError) => {
              n.error(error.message)
            }
          }
        )
      }
    })
  }

  return (
    <>
      <style>
        {`
          .current-day-cell::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: #3551f2;
            opacity: 0.1;
            pointer-events: none;
            z-index: 1;
          }

          .current-day-cell > * {
            position: relative;
            z-index: 2;
          }
        `}
      </style>
      <Box sx={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            border: '1px solid #e0e0e0'
          }}
        >
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5' }}>
              <th
                style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  borderRight: '1px solid #e0e0e0',
                  borderBottom: '1px solid #e0e0e0',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}
              >
                {__('Employee')}
              </th>
              {headerWorkingList.map((item, index) => {
                const isCurrentDay = item.day_of_month
                  ? dayjs().isSame(dayjs(item.day_of_month), 'day')
                  : currentDay !== null && item.date === currentDay

                const minimumWorkingDays = dataNormal?.minimum_working_days
                const isMinimumWorkingDayColumn = minimumWorkingDays && index < minimumWorkingDays
                const withdrawCutoffDay = Number(dataNormal?.company?.withdraw_cutoff_day || 0)
                const currentDayOfMonth = item.day_of_month ? dayjs(item.day_of_month).date() : Number(item.date)
                const isWithdrawCutoffBlocked = withdrawCutoffDay > 0 && currentDayOfMonth > withdrawCutoffDay
                const backgroundColor = isMinimumWorkingDayColumn || isWithdrawCutoffBlocked ? '#d5d4d8' : undefined

                return (
                  <th
                    key={`header-${item.day_of_month || item.date}`}
                    className={isCurrentDay ? 'current-day-cell' : ''}
                    style={{
                      padding: '12px 8px',
                      textAlign: 'center',
                      borderBottom: '1px solid #e0e0e0',
                      borderRight: '1px solid #e0e0e0',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      minWidth: '40px',
                      backgroundColor,
                      position: 'relative'
                    }}
                  >
                    {item.day_of_month ? dayjs(item.day_of_month).date() : item.date}
                  </th>
                )
              })}
              <th
                style={{
                  padding: '12px 16px',
                  textAlign: 'center',
                  borderBottom: '1px solid #e0e0e0',
                  fontWeight: 'bold',
                  fontSize: '14px',
                  minWidth: '150px'
                }}
              >
                {__('Worked Day This Month')}
              </th>
            </tr>
          </thead>
          {dataWithId.length > 0 && (
            <tbody>
              {dataWithId.map((employee, index) => (
                <tr
                  key={`${employee.id || employee.user_id || index}`}
                  style={{
                    borderBottom: '1px solid #e0e0e0',
                    backgroundColor: RowBackgroundForStatus[employee.status as keyof typeof RowBackgroundForStatus]
                  }}
                >
                  <td
                    style={{
                      padding: '12px 16px',
                      borderRight: '1px solid #e0e0e0',
                      verticalAlign: 'middle'
                    }}
                  >
                    <div className='flex items-center gap-2 justify-between'>
                      <div className='flex items-center gap-2'>
                        <Tooltip
                          title={
                            employee.status === RowBackgroundForStatusEnum.MissingFile
                              ? __('This employee exists in the system but is not in the file')
                              : employee.status === RowBackgroundForStatusEnum.MissingSystem
                                ? __('This employee is in the file but not in the system')
                                : ''
                          }
                          arrow
                          slots={{
                            transition: Zoom
                          }}
                          placement='right'
                        >
                          <div>
                            <UserAvatar
                              user={employee as any}
                              link={
                                employee.status === RowBackgroundForStatusEnum.MissingSystem &&
                                employee.user_id === null
                                  ? 'javascript:void(0)'
                                  : `/cms/attendance/employee/${employee.user_id}?month=${searchQuery.get('month')}&is_attendance_detail=true&companyId=${searchQuery.get('companyId') || id}`
                              }
                            />
                          </div>
                        </Tooltip>
                        {Number(employee?.daily_wage) <= 0 && (
                          <i className='tabler-info-circle text-[#ff9f43]' style={{ fontSize: 18 }} />
                        )}
                      </div>
                      {employee.is_allow_delete && (
                        <div>
                          <IconButton onClick={() => handleDeleteEmployee(employee?.id)}>
                            <i className='tabler-trash text-[#2F2B3DB2] size-[18px]' />
                          </IconButton>
                        </div>
                      )}
                    </div>
                  </td>
                  {headerWorkingList.map((item, dayIndex) => {
                    const day = item.date
                    const found = employee.attendance.find(a => a.date === day)
                    const status = found?.status
                    const color = status ? getAttendanceDotColor(status) : ''

                    const isCurrentDay = item.day_of_month
                      ? dayjs().isSame(dayjs(item.day_of_month), 'day')
                      : currentDay !== null && day === currentDay

                    const minimumWorkingDays = dataNormal?.minimum_working_days
                    const isMinimumWorkingDayColumn = minimumWorkingDays && dayIndex < minimumWorkingDays
                    const withdrawCutoffDay = Number(dataNormal?.company?.withdraw_cutoff_day || 0)
                    const currentDayOfMonth = item.day_of_month ? dayjs(item.day_of_month).date() : Number(day)
                    const isWithdrawCutoffBlocked = withdrawCutoffDay > 0 && currentDayOfMonth > withdrawCutoffDay
                    const backgroundColor = isMinimumWorkingDayColumn || isWithdrawCutoffBlocked ? '#2e263d14' : undefined

                    return (
                      <td
                        key={`${employee.id || employee.user_id || index}-day-${item.day_of_month || day}`}
                        className={isCurrentDay ? 'current-day-cell' : ''}
                        style={{
                          padding: '8px',
                          textAlign: 'center',
                          borderRight: '1px solid #e0e0e0',
                          verticalAlign: 'middle',
                          backgroundColor,
                          position: 'relative'
                        }}
                      >
                        {status ? (
                          <div
                            style={{
                              width: '16px',
                              height: '16px',
                              borderRadius: '50%',
                              backgroundColor: color,
                              margin: '0 auto',
                              display: 'inline-block',
                              cursor: 'pointer',
                              transition: 'transform 0.2s, box-shadow 0.2s'
                            }}
                            // onClick={() => handleUpdateAttendanceStatus(Number(employee.user_id), employee.attendance, day)}
                            // onMouseEnter={(e) => {
                            //   e.currentTarget.style.transform = 'scale(1.2)'
                            //   e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)'
                            // }}
                            // onMouseLeave={(e) => {
                            //   e.currentTarget.style.transform = 'scale(1)'
                            //   e.currentTarget.style.boxShadow = 'none'
                            // }}
                          />
                        ) : null}
                      </td>
                    )
                  })}
                  <td
                    style={{
                      padding: '12px 16px',
                      textAlign: 'center',
                      verticalAlign: 'middle',
                      fontWeight: 'bold'
                    }}
                  >
                    {countWorkingDays(employee.attendance)}
                  </td>
                </tr>
              ))}
            </tbody>
          )}
          {dataWithId.length === 0 && (
            <tbody>
              <tr>
                <td colSpan={headerWorkingList.length + 2} style={{ textAlign: 'center' }}>
                  <Box sx={{ p: 5, textAlign: 'center' }}>
                    <div className='h-[200px] flex items-center justify-center rounded-md'>
                      <div className='text-[15px] font-normal text-[#2f2b3de6]'>{__('No data found')}</div>
                    </div>
                  </Box>
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </Box>
      {meta && (
        <TablePagination
          meta={
            {
              ...meta,
              page: Number(searchQuery.get('page')) || 1
            } as unknown as TablePaginationProps
          }
        />
      )}
      <ConfirmDialog />
    </>
  )
}
